import * as React from 'react'
import {Lang} from 'yougile-lang'
import {UIUtil, Util} from 'yougile-util'
import {
  CheckEmailReq,
  CheckEmailResp,
  disabledUserId,
  KeyReq,
  KeyResp,
  RegisterReq,
  RegisterResp
} from 'yougile-model'
import {LoginHelper} from '@components/login/login-helper'
import {Backend} from '../../common/backend'
import {FileChooser} from '../../common/file-chooser'
import {ClientUtilities} from '../client/client-utilities'
import {FileUploadProgress} from './file-upload-progress'
import {MessageBox} from './message-box'

interface Props {
  withSteps?: boolean
}

interface State {
  step?: number
  photo?: string
  wait?: boolean
  error?: string
  errorField?: 'email' | 'name' | 'password'
}

type KeyboardEvent = React.KeyboardEvent
type MouseEvent = React.MouseEvent

export class RegistrationForm extends React.Component<Props, State> {
  public emailInput: HTMLInputElement
  public nameInput: HTMLInputElement
  public passwordInput: HTMLInputElement

  constructor(props: Props) {
    super(props)
    this.state = {
      step: 1,
      photo: '',
      wait: false,
      error: '',
      errorField: undefined
    }
  }

  public keyDown = (e: KeyboardEvent) => {
    if (e.keyCode) {
      this.setState({error: '', errorField: undefined})
    }
    if (e.keyCode === 13) {
      this.submit()
    }
  }

  public submit = () => {
    const email = this.emailInput.value
    const name = this.nameInput.value
    const password = this.passwordInput.value

    if (!Util.isEmail(email)) {
      this.setState({error: Lang.msg.emailIncorrect, errorField: 'email'})
      return
    }

    if (this.props.withSteps && this.state.step === 1) {
      this.setState({wait: true})
      const ceReq: CheckEmailReq = {email}
      const ceUrl = '/data/register/check-email'

      Backend.postJSON<CheckEmailResp>(ceUrl, ceReq)
        .then(res => {
          if (!res.foundUserId) {
            this.setState({step: 2, wait: false})
          } else {
            this.setState({
              error:
                res.foundUserId === disabledUserId
                  ? 'Failed to register user'
                  : 'User with this email already exists',
              errorField: 'email',
              wait: false
            })
          }
        })
        .catch(error =>
          this.setState({error, errorField: 'email', wait: false})
        )
      return
    }

    if (!name) {
      this.setState({error: Lang.msg.nameEmpty, errorField: 'name'})
      return
    }
    if (name.length < 3) {
      this.setState({error: Lang.msg.nameShort, errorField: 'name'})
      return
    }
    if (password.length < 3) {
      this.setState({error: Lang.msg.passwordShort, errorField: 'password'})
      return
    }
    this.setState({wait: true})

    const regReq: RegisterReq = {
      email,
      name,
      password,
      photo: this.state.photo
    }
    Backend.postJSON<RegisterResp>('/data/register', regReq)
      .then(res => {
        const keyReq: KeyReq = {password, userId: res.userId}
        return Backend.postJSON<KeyResp>('/data/key', keyReq)
      })
      .then(json => {
        if (json.nextStep && json.userId && json.key) {
          LoginHelper.set2FaDataToStorage({
            twoFactorStep: json.nextStep,
            loginFormEmail: email,
            loginFormPassword: password,
            userId: json.userId,
            authKey: json.key
          })

          location.href = '/team/login'

          return
        }

        Backend.saveCredentials(json.userId, json.key)
        location.href = '/team/'
      })
      .catch(error =>
        this.setState({
          wait: false,
          error,
          errorField: undefined
        })
      )
  }

  public uploadPhoto = (e: MouseEvent) => {
    void FileChooser.chooseFile(e, false, (file: File) => {
      if (!UIUtil.isSupportedImage(file)) {
        MessageBox.warn(Lang.msg.imageTypeUnsupported)
        return
      }
      const id = Util.UUID()
      FileUploadProgress.addFile(id, file)
      void ClientUtilities.saveFile(
        {file},
        (url: string) => {
          FileUploadProgress.removeFile(id)
          if (!url) {
            return
          }
          this.setState({photo: url})
        },
        (progress: number) => {
          FileUploadProgress.updateProgress(id, progress)
        }
      )
    })
  }

  public getPhotoStyle(): {backgroundImage: string} {
    if (!this.state.photo) {
      return undefined
    } else {
      const avatar = this.state.photo
      return {
        backgroundImage: avatar.includes('_____previews_')
          ? 'url("' + avatar.split('_____previews_')[0] + '-256-preview.png")'
          : 'url("' + avatar + '")'
      }
    }
  }

  public override componentDidUpdate(_: Props, prevState: State): void {
    if (this.state.step === 2 && prevState.step === 1) {
      this.nameInput.focus()
    }
    if (this.state.error) {
      const input = document.querySelector<HTMLInputElement>(
        '.reg-form-error-field ._input'
      )
      input.focus()
    }
  }

  public override render(): React.ReactNode {
    const error = (field?: 'email' | 'name' | 'password') =>
      this.state.error && this.state.errorField === field ? (
        <div className="reg-form-error">
          <i className="fa fa-warning" /> {this.state.error}
        </div>
      ) : undefined

    const errorClass = (field: 'email' | 'name' | 'password') =>
      this.state.errorField === field ? 'reg-form-error-field' : ''

    if (this.props.withSteps) {
      return (
        <form className={'reg-form step' + this.state.step}>
          <div className="reg-form-step1">
            <div className="reg-form-title">{Lang.msg.startTrial}</div>
            <label className={errorClass('email')}>
              <input
                type="email"
                className="_input"
                ref={(e: HTMLInputElement) => (this.emailInput = e)}
                onKeyDown={this.keyDown}
                disabled={this.state.wait}
                placeholder={Lang.msg.emailPlaceholder}
              />
              {error('email')}
            </label>
            <div
              tabIndex={0}
              onKeyDown={this.keyDown}
              className={Util.className({
                '_button reg-form-sign-up': true,
                disabled: this.state.wait
              })}
              onClick={this.submit}
            >
              {Lang.msg.go}
            </div>
            <div className="reg-form-already-have-acc">
              {Lang.msg.haveAcc}
              &nbsp;
              <a className="_link" href="/team/">
                {Lang.msg.signIn}
              </a>
            </div>
          </div>
          <div className="reg-form-step2">
            <div className="reg-form-title">{Lang.msg.registerLastStep}</div>
            <div className="ref-form-photo" onClick={this.uploadPhoto}>
              <div
                className="reg-form-photo-preview"
                style={this.getPhotoStyle()}
              />
              <div className="reg-form-photo-sub">
                <span className="_link reg-form-photo-sub-link">
                  {Lang.msg.uploadPhoto}
                </span>
                <br />
                {Lang.msg.optional}
              </div>
            </div>
            <div className="reg-form-right-cnt">
              <label className={errorClass('name')}>
                <input
                  type="text"
                  className="_input"
                  ref={(e: HTMLInputElement) => (this.nameInput = e)}
                  onKeyDown={this.keyDown}
                  disabled={this.state.wait}
                  placeholder={Lang.msg.namePlaceholder}
                />
                {error('name')}
              </label>
              <label className={errorClass('password')}>
                <input
                  type="password"
                  className="_input"
                  ref={(e: HTMLInputElement) => (this.passwordInput = e)}
                  onKeyDown={this.keyDown}
                  disabled={this.state.wait}
                  placeholder={Lang.msg.passwordPlaceholder}
                />
                {error('password')}
              </label>
              <div
                className={Util.className({
                  '_button reg-form-sign-up': true,
                  disabled: this.state.wait
                })}
                onClick={this.submit}
              >
                {Lang.msg.startUsing}
              </div>
            </div>
          </div>
          {error()}
        </form>
      )
    } else {
      return (
        <form className="reg-form no-steps">
          <div className="reg-form-no-steps">
            <div className="reg-form-title">{Lang.msg.registration}</div>
            <div className="ref-form-photo" onClick={this.uploadPhoto}>
              <div
                className="reg-form-photo-preview"
                style={this.getPhotoStyle()}
              />
              <div className="reg-form-photo-sub">
                <span className="_link reg-form-photo-sub-link">
                  {Lang.msg.uploadPhoto}
                </span>
                <br />
                {Lang.msg.optional}
              </div>
            </div>
            <div className="reg-form-right-cnt">
              <label className={errorClass('email')}>
                <input
                  type="email"
                  className="_input"
                  ref={(e: HTMLInputElement) => (this.emailInput = e)}
                  onKeyDown={this.keyDown}
                  disabled={this.state.wait}
                  placeholder={Lang.msg.emailPlaceholder}
                />
                {error('email')}
              </label>
              <label className={errorClass('name')}>
                <input
                  type="text"
                  className="_input"
                  ref={(e: HTMLInputElement) => (this.nameInput = e)}
                  onKeyDown={this.keyDown}
                  disabled={this.state.wait}
                  placeholder={Lang.msg.namePlaceholder}
                />
                {error('name')}
              </label>
              <label className={errorClass('password')}>
                <input
                  type="password"
                  className="_input"
                  ref={(e: HTMLInputElement) => (this.passwordInput = e)}
                  onKeyDown={this.keyDown}
                  disabled={this.state.wait}
                  placeholder={Lang.msg.passwordPlaceholder}
                />
                {error('password')}
              </label>
              <div
                tabIndex={0}
                onKeyDown={this.keyDown}
                className={Util.className({
                  '_button reg-form-sign-up': true,
                  disabled: this.state.wait
                })}
                onClick={this.submit}
              >
                {Lang.msg.doRegister}
              </div>
            </div>
          </div>
          {error()}
        </form>
      )
    }
  }
}
